import { AdminAmenityList } from '@/presentation/components/organisms/AdminAmenityList';
import { AdminAnnouncementList } from '@/presentation/components/organisms/AdminAnnouncementList';
import { AdminProjectList } from '@/modules/transparency/ui/components';
import { AdminAuditList } from '@/presentation/components/organisms/AdminAuditList';
import { AdminNotificationList } from '@/presentation/components/organisms/AdminNotificationList';
import { AdminNotificationConfigList } from '@/presentation/components/organisms/AdminNotificationConfigList';
import { AdminDeviceList } from '@/presentation/components/organisms/AdminDeviceList';
import { AdminRfidList } from '@/presentation/components/organisms/AdminRfidList';
import { AdminExpenseList } from '@/presentation/components/organisms/AdminExpenseList';
import { AdminCategoryList } from '@/presentation/components/organisms/AdminCategoryList';
import { FinanceDashboardPanel } from '@/presentation/components/organisms/admin-v2/FinanceDashboardPanel';
import { AdminChargeList } from '@/presentation/components/organisms/AdminChargeList';
import { AdminPaymentVerificationList } from '@/presentation/components/organisms/AdminPaymentVerificationList';
import { AdminLPRList } from '@/presentation/components/organisms/AdminLPRList';
import { AdminReservationList } from '@/presentation/components/organisms/AdminReservationList';
import { AdminResidentList } from '@/presentation/components/organisms/AdminResidentList';
import { AdminRoleList } from '@/presentation/components/organisms/AdminRoleList';
import { AdminSettings } from '@/presentation/components/organisms/AdminSettings';
import { AdminStaffList } from '@/presentation/components/organisms/AdminStaffList';
import { AdminUnitList } from '@/presentation/components/organisms/AdminUnitList';
import { AdminRoutinesPage } from '@/modules/routines/ui/pages/AdminRoutinesPage';
import { AdminSections } from '@/presentation/components/organisms/AdminSections';
import { useAbility, type Permission } from '@/modules/identity/application/ability';
import { useI18n } from '@/shared/i18n';
import { useServices } from '@/app/providers';
import { canUseAdminLayoutV2 } from '@/shared/navigation/canUseAdminLayoutV2';
import {
  ArrowLeft,
  Bell,
  Building,
  Calendar,
  Camera,
  ChevronRight,
  ClipboardList,
  CreditCard,
  FolderKanban,
  Home,
  Key,
  Layers,
  PieChart,
  Send,
  Settings,
  Shield,
  UserCog,
  Users
} from 'lucide-react';
import { Routes, Route, useNavigate, Link, Navigate } from 'react-router-dom';

// Admin route configuration
export const ADMIN_ROUTES = {
  menu: '/admin',
  unidades: '/admin/unidades',
  secciones: '/admin/secciones',
  residentes: '/admin/residentes',
  amenidades: '/admin/amenidades',
  reservas: '/admin/reservas',
  finanzas: '/admin/finanzas',
  'resumen-finanzas': '/admin/resumen-finanzas',
  egresos: '/admin/egresos',
  categorias: '/admin/categorias',
  facturacion: '/admin/facturacion',
  'puntos-acceso': '/admin/puntos-acceso',
  'camaras-lpr': '/admin/camaras-lpr',
  'tarjetas-rfid': '/admin/tarjetas-rfid',
  comunicados: '/admin/comunicados',
  transparencia: '/admin/transparencia',
  notificaciones: '/admin/notificaciones',
  'notificaciones-config': '/admin/notificaciones-config',
  personal: '/admin/personal',
  roles: '/admin/roles',
  configuracion: '/admin/configuracion',
  auditoria: '/admin/auditoria',
} as const;

const AdminCard = ({ title, desc, icon: Icon, to, color }: {
  title: string;
  desc: string;
  icon: any;
  to: string;
  color: string;
}) => (
  <Link
    to={to}
    className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-all text-left group flex flex-col justify-between h-48"
  >
    <div className={`p-3 rounded-lg w-fit mb-4 ${color} text-white`}>
      <Icon size={24} />
    </div>
    <div>
      <h3 className="font-bold text-slate-800 text-lg group-hover:text-primary-600 transition-colors flex items-center gap-2">
        {title}
        <ChevronRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity transform group-hover:translate-x-1" />
      </h3>
      <p className="text-sm text-slate-500 mt-1 leading-relaxed">{desc}</p>
    </div>
  </Link>
);

const SectionTitle = ({ title }: { title: string }) => (
  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 mt-2 px-1">{title}</h3>
);

const canAccessPermission = (
  ability: ReturnType<typeof useAbility>,
  permission?: Permission,
) => {
  if (!permission) return true;
  if (ability.can(permission.action, permission.subject as any)) return true;
  if (ability.can('manage', 'all')) return true;
  return false;
};

// Admin Menu (index page)
const AdminMenu = () => {
  const { t } = useI18n();
  const ability = useAbility();
  const canAccess = (permission: Permission) => canAccessPermission(ability, permission);

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div>
        <h2 className="text-2xl font-bold text-slate-800">{t('admin.title')}</h2>
        <p className="text-slate-500">{t('admin.section.platform')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">

        <div className="col-span-full">
          <SectionTitle title={t('admin.section.community')} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <AdminCard
              title={t('admin.card.units')}
              desc={t('admin.card.units.desc')}
              icon={Building}
              color="bg-indigo-500"
              to={ADMIN_ROUTES.unidades}
            />
            {canAccess({ action: 'read', subject: 'Section' }) && (
              <AdminCard
                title="Secciones"
                desc="Subdivisiones operativas del residencial (torres, áreas)"
                icon={Layers}
                color="bg-teal-500"
                to={ADMIN_ROUTES.secciones}
              />
            )}
            {canAccess({ action: 'read', subject: 'ResidentMenu' }) && (
              <AdminCard
                title={t('admin.card.residents')}
                desc={t('admin.card.residents.desc')}
                icon={Users}
                color="bg-blue-500"
                to={ADMIN_ROUTES.residentes}
              />
            )}
            <AdminCard
              title={t('admin.card.amenities')}
              desc={t('admin.card.amenities.desc')}
              icon={Home}
              color="bg-pink-500"
              to={ADMIN_ROUTES.amenidades}
            />
            <AdminCard
              title={t('admin.card.reservations')}
              desc={t('admin.card.reservations.desc')}
              icon={Calendar}
              color="bg-violet-500"
              to={ADMIN_ROUTES.reservas}
            />
          </div>
        </div>

        <div className="col-span-full">
          <SectionTitle title={t('admin.section.finance')} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <AdminCard
              title={t('admin.finance.title')}
              desc="Libros contables, generación de cuotas masivas y control de saldos."
              icon={PieChart}
              color="bg-cyan-600"
              to={ADMIN_ROUTES.finanzas}
            />
            <AdminCard
              title={t('admin.card.expenses')}
              desc={t('admin.card.expenses.desc')}
              icon={PieChart}
              color="bg-teal-500"
              to={ADMIN_ROUTES.egresos}
            />
            <AdminCard
              title={t('admin.card.payments')}
              desc={t('admin.card.payments.desc')}
              icon={CreditCard}
              color="bg-emerald-500"
              to={ADMIN_ROUTES.facturacion}
            />
          </div>
        </div>

        <div className="col-span-full">
          <SectionTitle title={t('admin.section.security')} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <AdminCard
              title={t('admin.card.access')}
              desc={t('admin.card.access.desc')}
              icon={Shield}
              color="bg-rose-500"
              to={ADMIN_ROUTES['puntos-acceso']}
            />
            <AdminCard
              title={t('admin.card.lpr')}
              desc={t('admin.card.lpr.desc')}
              icon={Camera}
              color="bg-orange-500"
              to={ADMIN_ROUTES['camaras-lpr']}
            />
            <AdminCard
              title="Tarjetas RFID"
              desc="Gestión de credenciales de acceso por tarjeta RFID."
              icon={CreditCard}
              color="bg-purple-500"
              to={ADMIN_ROUTES['tarjetas-rfid']}
            />
          </div>
        </div>

        <div className="col-span-full">
          <SectionTitle title={t('admin.section.platform')} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <AdminCard
              title={t('admin.card.announcements')}
              desc={t('admin.card.announcements.desc')}
              icon={Bell}
              color="bg-amber-500"
              to={ADMIN_ROUTES.comunicados}
            />
            <AdminCard
              title="Transparencia"
              desc="Proyectos de mejora comunitaria: avance, timeline y galería."
              icon={FolderKanban}
              color="bg-teal-500"
              to={ADMIN_ROUTES.transparencia}
            />
            <AdminCard
              title={t('notifications.title')}
              desc="Envío de notificaciones push, email, SMS y más."
              icon={Send}
              color="bg-cyan-500"
              to={ADMIN_ROUTES.notificaciones}
            />
            {canAccess({ action: 'read', subject: 'StaffMenu' }) && (
              <AdminCard
                title={t('admin.card.staff')}
                desc={t('admin.card.staff.desc')}
                icon={UserCog}
                color="bg-purple-500"
                to={ADMIN_ROUTES.personal}
              />
            )}
            {canAccess({ action: 'read', subject: 'RoleMenu' }) && (
              <AdminCard
                title={t('admin.card.roles')}
                desc={t('admin.card.roles.desc')}
                icon={Key}
                color="bg-indigo-600"
                to={ADMIN_ROUTES.roles}
              />
            )}
            <AdminCard
              title={t('admin.card.settings')}
              desc={t('admin.card.settings.desc')}
              icon={Settings}
              color="bg-slate-600"
              to={ADMIN_ROUTES.configuracion}
            />
            <AdminCard
              title="Auditoría"
              desc="Registro de actividad y cambios en el sistema."
              icon={ClipboardList}
              color="bg-slate-800"
              to={ADMIN_ROUTES.auditoria}
            />
          </div>
        </div>

      </div>
    </div>
  );
};

// Wrapper for admin sub-pages with back button
const AdminSubPage = ({ children, permission }: { children: React.ReactNode; permission?: Permission }) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { currentUser } = useServices();
  const ability = useAbility();
  const showBack = !canUseAdminLayoutV2(ability, currentUser?.tenantRole);

  if (!canAccessPermission(ability, permission)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="animate-in slide-in-from-right-8 duration-300 space-y-6">
      {showBack && (
        <button
          onClick={() => navigate(ADMIN_ROUTES.menu)}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors mb-4 group"
        >
          <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">{t('common.back')}</span>
        </button>
      )}
      {children}
    </div>
  );
};

export const AdminPanel = () => {
  return (
    <Routes>
      <Route index element={<AdminMenu />} />
      <Route path="unidades" element={<AdminSubPage permission={{ action: 'read', subject: 'Unit' }}><AdminUnitList /></AdminSubPage>} />
      <Route path="secciones" element={<AdminSubPage permission={{ action: 'read', subject: 'Section' }}><AdminSections /></AdminSubPage>} />
      <Route path="residentes" element={<AdminSubPage permission={{ action: 'read', subject: 'ResidentMenu' }}><AdminResidentList /></AdminSubPage>} />
      <Route path="amenidades" element={<AdminSubPage permission={{ action: 'read', subject: 'Amenity' }}><AdminAmenityList /></AdminSubPage>} />
      <Route path="reservas" element={<AdminSubPage permission={{ action: 'read', subject: 'Reservation' }}><AdminReservationList /></AdminSubPage>} />
      <Route path="finanzas" element={<AdminSubPage permission={{ action: 'read', subject: 'Charge' }}><AdminChargeList /></AdminSubPage>} />
      <Route path="resumen-finanzas" element={<AdminSubPage permission={{ action: 'read', subject: 'Summary' }}><FinanceDashboardPanel /></AdminSubPage>} />
      <Route path="egresos" element={<AdminSubPage permission={{ action: 'read', subject: 'Expense' }}><AdminExpenseList /></AdminSubPage>} />
      <Route path="categorias" element={<AdminSubPage permission={{ action: 'read', subject: 'Category' }}><AdminCategoryList /></AdminSubPage>} />
      <Route path="facturacion" element={<AdminSubPage permission={{ action: 'read', subject: 'Payment' }}><AdminPaymentVerificationList /></AdminSubPage>} />
      <Route path="puntos-acceso" element={<AdminSubPage permission={{ action: 'read', subject: 'AccessDevice' }}><AdminDeviceList /></AdminSubPage>} />
      <Route path="camaras-lpr" element={<AdminSubPage permission={{ action: 'read', subject: 'LicensePlate' }}><AdminLPRList /></AdminSubPage>} />
      <Route path="tarjetas-rfid" element={<AdminSubPage permission={{ action: 'read', subject: 'RfidCredential' }}><AdminRfidList /></AdminSubPage>} />
      <Route path="comunicados" element={<AdminSubPage permission={{ action: 'read', subject: 'Announcement' }}><AdminAnnouncementList /></AdminSubPage>} />
      <Route path="transparencia" element={<AdminSubPage permission={{ action: 'read', subject: 'TransparencyProject' }}><AdminProjectList /></AdminSubPage>} />
      <Route path="notificaciones" element={<AdminSubPage permission={{ action: 'read', subject: 'Notification' }}><AdminNotificationList /></AdminSubPage>} />
      <Route path="notificaciones-config" element={<AdminSubPage permission={{ action: 'read', subject: 'NotificationConfig' }}><AdminNotificationConfigList /></AdminSubPage>} />
      <Route path="personal" element={<AdminSubPage permission={{ action: 'read', subject: 'StaffMenu' }}><AdminStaffList /></AdminSubPage>} />
      <Route path="roles" element={<AdminSubPage permission={{ action: 'read', subject: 'RoleMenu' }}><AdminRoleList /></AdminSubPage>} />
      <Route path="configuracion" element={<AdminSubPage permission={{ action: 'read', subject: 'Settings' }}><AdminSettings /></AdminSubPage>} />
      <Route path="auditoria" element={<AdminSubPage permission={{ action: 'read', subject: 'AuditLog' }}><AdminAuditList /></AdminSubPage>} />
      <Route path="routines" element={<AdminSubPage permission={{ action: 'read', subject: 'Routine' }}><AdminRoutinesPage /></AdminSubPage>} />
    </Routes>
  );
};
